import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { 
  X, 
  Copy, 
  Check, 
  ArrowUpRight, 
  Users, 
  BookOpen, 
  Send,
  Sparkles
} from 'lucide-react';
import { buildApiUrl } from '../config/api';

interface GettingStartedModalProps {
  isOpen: boolean;
  onClose: () => void;
  apiKey: string;
  organizationId: number;
  token: string;
  projectName: string;
}

export default function GettingStartedModal({ 
  isOpen, 
  onClose, 
  apiKey, 
  organizationId, 
  token,
  projectName 
}: GettingStartedModalProps) {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [invitesSent, setInvitesSent] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [promptCopied, setPromptCopied] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  
  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  // Handle fade-in when modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready, then trigger fade-in
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Handle close with fade-out animation
  const handleClose = () => {
    setIsAnimatingOut(true);
    setIsVisible(false);
    setTimeout(() => {
      setIsAnimatingOut(false);
      onClose();
    }, 200); // Match transition duration
  };

  if (!isOpen && !isAnimatingOut) return null;

  const llmPrompt = `Implement Turret analytics in this codebase.

## What is Turret?
Turret is a product analytics API for AI/LLM applications. It:
- Semantically clusters user messages by topic (e.g., "password reset requests", "billing questions")
- Tracks user journeys through topics within conversations

## Your Task
1. Find all places where users send messages to an LLM (OpenAI, Anthropic, etc.)
2. Track ONLY the user's message/prompt to Turret. Do NOT track: system prompts, LLM responses, or any other data.
3. If you cannot isolate just the user message, stop and ask me.

## API Details
- Endpoint: POST https://api.useturret.com/track
- API Key: ${apiKey}
- Auth Header: X-API-Key

## Request Format
\`\`\`json
{
  "name": "user_message",
  "session_id": "optional-conversation-id",
  "user_id": "optional-user-id",
  "metadata": {
    "prompt": "the user's message text"
  }
}
\`\`\`

## Response Format
\`\`\`json
{
  "session_id": "uuid",
  "user_id": "uuid"
}
\`\`\`

## CRITICAL: Session ID Handling
The session_id connects messages into a conversation journey. You MUST handle it correctly:

**Option A - If this app already has conversation IDs:**
Pass your existing conversation/thread ID as session_id. Most chat apps already track this.

**Option B - If this app does NOT have conversation IDs:**
1. First message: Omit session_id (or pass null)
2. Store the session_id from the response
3. All subsequent messages in that conversation: Include the stored session_id

## Implementation Pattern
\`\`\`javascript
// Create a module/service for Turret tracking
class TurretTracker {
  constructor() {
    this.sessionId = null;
    this.userId = null;
  }

  async trackUserMessage(userMessage, existingSessionId = null, existingUserId = null) {
    const response = await fetch('https://api.useturret.com/track', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': '${apiKey}'
      },
      body: JSON.stringify({
        name: 'user_message',
        session_id: existingSessionId || this.sessionId,
        user_id: existingUserId || this.userId,
        metadata: {
          prompt: userMessage
        }
      })
    });

    const data = await response.json();
    // Store for subsequent messages in this conversation
    this.sessionId = data.session_id;
    this.userId = data.user_id;
    return data;
  }

  // Call when starting a new conversation
  newConversation() {
    this.sessionId = null;
  }
}
\`\`\`

## Where to Call It
Call trackUserMessage() right before or after sending the user's message to the LLM. Example:

\`\`\`javascript
async function handleUserMessage(userMessage) {
  // Track to Turret (fire-and-forget is fine)
  turretTracker.trackUserMessage(userMessage, conversationId, userId);

  // Send to LLM as usual
  const response = await callLLM(userMessage);
  return response;
}
\`\`\`

## Checklist
- [ ] Found all LLM interaction points
- [ ] Created Turret tracking module/function
- [ ] Passing ONLY the user's message text (not system prompts or responses)
- [ ] Session ID is persisted across messages in the same conversation
- [ ] Tracking call doesn't block the main UX (async/fire-and-forget)

Documentation: https://docs.useturret.com`;

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(llmPrompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const addEmail = (email: string) => {
    const trimmed = email.trim().toLowerCase();
    if (trimmed && isValidEmail(trimmed) && !emails.includes(trimmed)) {
      setEmails([...emails, trimmed]);
      setEmailInput('');
    }
  };

  const removeEmail = (emailToRemove: string) => {
    setEmails(emails.filter(e => e !== emailToRemove));
  };

  const handleEmailKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === 'Tab') {
      e.preventDefault();
      addEmail(emailInput);
    } else if (e.key === 'Backspace' && !emailInput && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handleEmailPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const pastedEmails = pastedText.split(/[,;\s\n]+/).filter(email => email.trim());
    const newEmails = [...emails];
    pastedEmails.forEach(email => {
      const trimmed = email.trim().toLowerCase();
      if (trimmed && isValidEmail(trimmed) && !newEmails.includes(trimmed)) {
        newEmails.push(trimmed);
      }
    });
    setEmails(newEmails);
    setEmailInput('');
  };

  const handleSendInvites = async () => {
    if (emails.length === 0) return;
    
    setSendingInvites(true);
    setInviteError(null);

    try {
      const results = await Promise.allSettled(
        emails.map(email =>
          fetch(buildApiUrl(`api/v1/organizations/${organizationId}/invitations`), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              invitation: {
                email: email,
                role: 'member'
              }
            })
          })
        )
      );

      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (failed > 0) {
        setInviteError(`${failed} invitation(s) failed to send`);
      } else {
        setInvitesSent(true);
        setEmails([]);
      }
    } catch (err) {
      setInviteError('Failed to send invitations');
    } finally {
      setSendingInvites(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ease-out ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className={`relative w-full max-w-xl max-h-[90vh] overflow-y-auto mx-4 bg-white rounded-2xl shadow-2xl transition-all duration-200 ease-out ${
          isVisible 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-[0.98] translate-y-2'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-5 right-5 p-1.5 rounded-full hover:bg-black/5 text-black/40 hover:text-black/60 transition-colors z-10"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="px-10 pt-10 pb-2">
          <h2 className="text-3xl font-semibold text-black tracking-tight">
            Welcome to Turret
          </h2>
          <p className="text-black/50 mt-1">Get started in minutes</p>
        </div>

        <div className="px-10 pb-10 space-y-8">
          
          {/* Quick Start Section */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-4 w-4 text-purple-400" />
              <span className="text-xs font-medium text-purple-400 uppercase tracking-wide">Fastest way to start</span>
            </div>
            
            <div className="bg-neutral-200 rounded-xl p-5 shadow-md border border-neutral-300">
              <h3 className="text-neutral-800 font-medium mb-1">Copy to your AI coding assistant</h3>
              <p className="text-neutral-600 text-sm mb-4">
                Paste this prompt in Cursor or Claude Code to implement Turret automatically.
              </p>
              
              <div className="bg-neutral-100 rounded-lg p-4 mb-4 font-mono text-xs text-neutral-600 max-h-24 overflow-y-auto">
                <span className="text-purple-600">{`// Implementation prompt for project: ${projectName}`}</span>
                <br />
                {llmPrompt.slice(0, 150)}...
              </div>
              
              <button
                onClick={handleCopyPrompt}
                className={`w-full py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                  promptCopied 
                    ? 'bg-green-500 text-white' 
                    : 'bg-white text-black hover:bg-white/90'
                }`}
              >
                {promptCopied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied to clipboard
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy prompt
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Invite Team */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center">
                <Users className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-medium text-black">Invite your team</h3>
                <p className="text-sm text-black/50">Add engineers, PMs, or stakeholders</p>
              </div>
            </div>

            {invitesSent ? (
              <div className="flex items-center justify-center gap-2 py-6 text-green-600 bg-green-50 rounded-xl">
                <Check className="h-5 w-5" />
                <span className="font-medium">Invitations sent</span>
              </div>
            ) : (
              <>
                <div 
                  className="min-h-[56px] bg-black/[0.03] border border-black/10 rounded-xl p-3 flex flex-wrap gap-2 items-center cursor-text focus-within:border-purple-400 focus-within:ring-4 focus-within:ring-purple-400/10 transition-all"
                  onClick={() => emailInputRef.current?.focus()}
                >
                  {emails.map(email => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 bg-black text-white rounded-full text-sm"
                    >
                      {email}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeEmail(email);
                        }}
                        className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    ref={emailInputRef}
                    type="text"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={handleEmailKeyDown}
                    onPaste={handleEmailPaste}
                    onBlur={() => emailInput && addEmail(emailInput)}
                    placeholder={emails.length === 0 ? "name@company.com" : ""}
                    className="flex-1 min-w-[120px] bg-transparent text-black placeholder-black/30 outline-none text-sm"
                    disabled={sendingInvites}
                  />
                </div>
                
                <p className="text-xs text-black/40 mt-2">
                  Press Enter or comma to add. Paste multiple emails at once.
                </p>

                {inviteError && (
                  <p className="text-sm text-red-500 mt-2">{inviteError}</p>
                )}

                {emails.length > 0 && (
                  <button
                    onClick={handleSendInvites}
                    disabled={sendingInvites}
                    className="mt-4 w-full py-3 px-4 bg-black text-white rounded-xl font-medium text-sm hover:bg-black/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {sendingInvites ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send {emails.length} invitation{emails.length > 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Resources */}
          <div className="flex gap-3">
            <a
              href="https://discord.com/invite/kY6vQRTctp"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 group"
            >
              <div className="h-full p-4 rounded-xl border border-black/10 hover:border-purple-400/50 hover:bg-purple-400/[0.03] transition-all">
                <div className="flex items-center justify-between mb-2">
                  <svg className="w-5 h-5 text-black/70" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                  </svg>
                  <ArrowUpRight className="h-4 w-4 text-black/30 group-hover:text-purple-400 transition-colors" />
                </div>
                <p className="font-medium text-black text-sm">Discord</p>
                <p className="text-xs text-black/50">Join the community</p>
              </div>
            </a>

            <a
              href="https://docs.useturret.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 group"
            >
              <div className="h-full p-4 rounded-xl border border-black/10 hover:border-purple-400/50 hover:bg-purple-400/[0.03] transition-all">
                <div className="flex items-center justify-between mb-2">
                  <BookOpen className="h-5 w-5 text-black/70" />
                  <ArrowUpRight className="h-4 w-4 text-black/30 group-hover:text-purple-400 transition-colors" />
                </div>
                <p className="font-medium text-black text-sm">Documentation</p>
                <p className="text-xs text-black/50">Get started</p>
              </div>
            </a>
          </div>

          {/* Dismiss */}
          <button
            onClick={handleClose}
            className="w-full py-3 text-sm text-black/40 hover:text-black/60 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
